import { type ReactNode } from 'react'

import { Icon } from '@/components/icons/Icon'
import { usePage } from '@/components/providers/PageProvider'
import { type IconType } from '@/defs/icon'
import { COLOR_ID_LABELS, type ColorIdType } from '@/defs/mtg'
import { type RecType } from '@/defs/rec'
import { useCardsRec } from '@/hooks/useCardsRec'
import { getColorId } from '@/utils/cardUtil'
import { getRecName, isRecEqual } from '@/utils/recUtil'

import { StatCommonBox } from '../../StatCommon/StatCommonBox/StatCommonBox'
import { StatCommonFigure } from '../../StatCommon/StatCommonFigure/StatCommonFigure'

type Props = {
  isInView?: boolean
  rec: RecType
}

type VariantType = 'default' | 'defaultFiltered' | 'commander' | 'commanderFiltered'

type ConfigType = {
  description: ReactNode
  icon: IconType
  label: string
}

const colorIdLabel = (colorId: ColorIdType) => {
  const { icon, label } = COLOR_ID_LABELS[colorId]

  return (
    <span className='nowrap'>
      <Icon name={icon} /> <strong>{label}</strong>
    </span>
  )
}

const getVariant = (colorId: ColorIdType) => {
  const { icon, label } = COLOR_ID_LABELS[colorId]
  return { icon, label }
}

export const StatRecColorId = ({ isInView = true, rec }: Props) => {
  const { page } = usePage()
  const { card, cardPartner, loading } = useCardsRec({ enabled: isInView, rec })
  const name = <strong>{getRecName(rec)}</strong>

  const recColorId = getColorId(card?.colorId, cardPartner?.colorId)
  const isFeatured = isRecEqual(rec, page?.featured?.rec)
  const isFilterable = isFeatured && !!page?.optionsFilters?.colorIds
  const isCommanderPage = page?.route?.section === 'commandersFeatured'
  const filterColorId = isFilterable ? page.route?.colorId : undefined
  const colorId = filterColorId || recColorId
  const isPending = loading || !isInView

  const variant: VariantType =
    isFilterable && isCommanderPage
      ? filterColorId
        ? 'commanderFiltered'
        : 'commander'
      : filterColorId
        ? 'defaultFiltered'
        : 'default'

  const CONFIG: Record<VariantType, ConfigType> = {
    commander: {
      description: (
        <>
          All possible color identities with {name} as a commander, whose base color identity is {colorIdLabel(colorId)}
          . Filter by choosing a specific color identity below.
        </>
      ),
      icon: 'circleQuestion',
      label: 'Color ID',
    },
    commanderFiltered: {
      ...getVariant(colorId),
      description: (
        <>
          The currently selected color identity filter for {name} is {colorIdLabel(colorId)}, whose base color identity
          is {colorIdLabel(recColorId)}.
        </>
      ),
    },
    default: {
      ...getVariant(colorId),
      description: (
        <>
          The color identity of {name} is {colorIdLabel(recColorId)}.
        </>
      ),
    },
    defaultFiltered: {
      ...getVariant(colorId),
      description: (
        <>
          The color identity of {name} is {colorIdLabel(recColorId)}. The currently selected color identity filter is{' '}
          {colorIdLabel(colorId)}.
        </>
      ),
    },
  }

  const { description, icon, label } = CONFIG[variant]

  const popover = !isPending && (
    <>
      <h2>Color Identity</h2>
      <p>{description}</p>
    </>
  )

  return (
    <StatCommonBox
      isBlank={!isInView}
      isInView={isInView}
      label={isPending ? 'Color ID' : label}
      loading={loading}
      popover={popover}
      popoverPlacement='top'
    >
      {!isPending && (
        <StatCommonFigure>
          <Icon name={icon} />
        </StatCommonFigure>
      )}
    </StatCommonBox>
  )
}
